//--------------------------------------------------------------------------
// <copyright file="PairingCodeDialog.js" company="Microsoft">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
//--------------------------------------------------------------------------
import FluentPrimaryButton from './FluentPrimaryButton';
import FluentTextField from './FluentTextField';
import { useState } from 'react'
import MicrosoftLogo from './MicrosoftLogo';
import BackButton from './BackButton';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import PairingStates from '../models/PairingStatesEnum';
import { webApiServicePost } from "../services/webAPIServicePost";
import { webApiConfig, tokenRequest } from '../utils/authConfig';

/// <summary>
/// Helper methods and display components related to Pairing code dialog.
/// </summary>
const PairingCodeDialog = ({ username, setPairingState, pairingState, setPairingCode, pairingCode, logout, acquireToken, correlationId }) => {

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const pairDevicePostRequestCallback = async (response) => {
    if (response.ok) {
      setPairingState(PairingStates.PairingInProgress);
    }
    else {
      setPairingState(PairingStates.PairingCodeError);
      var responseMessage = await response.text();
      if (!responseMessage || responseMessage?.trim()?.length < 1)
      {
        responseMessage = 'We encountered some issue. Please try again in sometime'
      }
      setErrorMessage(responseMessage);
    }
  }

  const callPairDeviceWebApi = async () => {

    var token = null;
    try {
      token = await acquireToken(tokenRequest);
    } catch (error) {
      setPairingState(PairingStates.PairingCodeError);
      setErrorMessage('We encountered some issue. Please try again in sometime');
      return null;
    }

    try {
      const requestBody = JSON.stringify({
        PairingCode: pairingCode.toUpperCase()
      });
      const responsePair = await webApiServicePost(webApiConfig.BaseDeploymentURL + webApiConfig.webAPIPairDevicePath, token.accessToken, requestBody, correlationId);
      return await pairDevicePostRequestCallback(responsePair);

    } catch (error) {
      setPairingState(PairingStates.PairingCodeError);
      setErrorMessage('We encountered some issue. Please try again in sometime');
    }
  }

  const checkIfSpecialCharacters = () => {
    return !pairingCode.match("^[A-Za-z0-9]+$");
  }

  const handleFormSubmit = async () => {
    if (pairingCode.length != 9 || checkIfSpecialCharacters(pairingCode)) {
      setPairingState(PairingStates.PairingCodeError);
      setErrorMessage('Pairing code invalid or expired. Please regenerate the pairing code on device and try again.');
    }
    else {
      setIsButtonDisabled(true);
      setPairingState(PairingStates.PairingStatusPending);
      await callPairDeviceWebApi();
      setIsButtonDisabled(false);
    }
  };

  const handleButtonPress = async () => {
    await handleFormSubmit();
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      await handleFormSubmit();
    }
  };

  var paragraph = null;

  switch (pairingState) {
    case PairingStates.PairingCodeNotEntered:
      paragraph = <p data-testid="message" className="Dialog-paragraph">Enter the pair code shown on your SIP device</p>;
      break;
    case PairingStates.PairingCodeError:
      paragraph = <p role="alert" data-testid="message" className="Dialog-paragraph-error display-linebreak">{errorMessage}</p>;
      break;
    case PairingStates.PairingStatusPending:
      paragraph = <ProgressIndicator data-testid="loadingIndicator" className="Dialog-inprogress" styles={{ root: { width: '352px' }, progressTrack: { backgroundColor: '#FFFFFF', } }} />;
      break;
    default:
  }

  return (
    <div className="Dialog" data-testid="pairingCodeDialog">
      <MicrosoftLogo />
      <BackButton onClickFunction={logout} />
      <h1 className="Dialog-header">Enter pair code to add a device</h1>
      <p data-testid="username" className="Dialog-username">{username}</p>
      <div className="Dialog-flexible">
        {paragraph}
        <FluentTextField setInput={setPairingCode} input={pairingCode} onKeyDownFunction={handleKeyDown} placeholderText={"Enter pair code to add a device"} shouldAutoFocus={true} />
        <div className="Dialog-footer">
          <a className="Dialog-feedback" rel="noreferrer noopener" target="_blank" href="https://feedbackportal.microsoft.com/feedback/">Give feedback</a>
          <FluentPrimaryButton text='Pair' cssClassName='Dialog-button' setPairingState={setPairingState} pairCode={pairingCode} onClickFunction={handleButtonPress} isButtonDisabled={isButtonDisabled} />
        </div>
      </div>
    </div>
  )
}

export default PairingCodeDialog
